import { useState } from 'react';
import Toggle from '../../components/Toggle';
import appSettingsService, { type AppSetting } from '../../services/appSettings.service';

const ENABLED_KEY = 'samsara_route_eta.enabled';

export function RouteEtaSettings({ settings, onSaved }: { settings: AppSetting[]; onSaved: () => void }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const enabled = settings.find(s => s.setting_key === ENABLED_KEY)?.setting_value === 'true';

  async function save(key: string, value: string) {
    setSaving(true);
    setError('');
    try {
      await appSettingsService.update(key, value);
      onSaved();
    } catch {
      setError('Could not save Route ETA settings. Please try again.');
    } finally { setSaving(false); }
  }

  return (
    <section className="bg-panel shadow rounded-lg p-6 mt-6">
      <h2 className="text-lg font-medium text-heading mb-3">Samsara Route ETA comparison</h2>
      <p className="text-sm text-muted mb-4">
        Creates routes for all eligible drivers mapped to Samsara. The Sync project manages the routes
        in production. Users can show the comparison in Driver Status Monitor and PRO details by
        enabling Show Samsara Route ETAs in Application Preferences.
      </p>
      <div className="flex items-center gap-3">
        <Toggle checked={enabled} onChange={checked => save(ENABLED_KEY, String(checked))}
          disabled={saving} name={ENABLED_KEY}
          ariaLabel="Enable Samsara Route ETA comparison" />
        <span className="text-sm text-label">{enabled ? 'Enabled' : 'Disabled'}</span>
      </div>
      <p className="mt-2 text-xs text-muted">
        Routes need valid locations and a schedule. Missing appointment times use cached CMS projected
        stop times; drivers already in transit use their recorded departure time. Sync checks routes
        every five minutes. Initial fleet enrollment is gradual to limit API traffic.
        Turning this off stops syncing and hides the comparison; routes already created in Samsara remain there.
      </p>
      {error && <p role="alert" className="mt-2 text-sm text-red-600">{error}</p>}
    </section>
  );
}
